import { useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { yupResolver } from "@hookform/resolvers/yup";

import { Button } from "../../../../components/Button";
import { LineData } from "../../../../components/LineData";
import {
  type NaturalFormData,
  NaturalFormPresentational,
} from "../../../../components/Forms/NaturalFormPresentational";
import { formatCpf } from "../../../../utils/formatCpf";
import { getErrorMessage } from "../../../../utils/getErrorMessage";
import { naturalPersonSchema } from "../../../../validators/naturalPersonSchema";
import { type ExpertDomain } from "../../../../types/domain/expert-domain";
import { useExpertNaturalDataMutation } from "../../../../services/hooks/experts/mutations/useExpertNaturalDataMutation";
import {
  TOAST_ERROR_DEFAULT_OPTIONS,
  TOAST_SUCCESS_DEFAULT_OPTIONS,
} from "../../../../constants/toast";
import { useAuth } from "../../../../contexts/AuthContext";
import { useExpertQuery } from "../../../../services/hooks/experts/queries/useExpertQuery";
import { capitalize } from "../../../../utils/capitalize";
import { normalizeCpf } from "../../../../utils/normalizeCpf";

interface NaturalPersonEditViewProps {
  expert?: ExpertDomain;
}

export const NaturalPersonEditView = ({
  expert,
}: NaturalPersonEditViewProps) => {
  const { user } = useAuth();
  const { data } = useExpertQuery(user!.id);

  const [isFormVisible, setIsFormVisible] = useState(false);

  const { formatMessage } = useIntl();
  const { mutate, isLoading } = useExpertNaturalDataMutation();

  const methods = useForm({
    mode: "onChange",
    resolver: yupResolver(naturalPersonSchema),
    defaultValues: {
      cpf: data?.cpf,
      name: data?.name,
      email: data?.email,
      phone: data?.phone,
      linkedin: data?.linkedin,
      addressNumber: data?.naturalData?.addressNumber,
      birthdate: data?.naturalData?.birthdate,
      rg: data?.naturalData?.rg,
      cep: data?.naturalData?.cep,
      street: data?.naturalData?.street,
      complement: data?.naturalData?.complement || "",
      neighborhood: data?.naturalData?.neighborhood,
      city: data?.naturalData?.city,
      state: data?.naturalData?.state,
    },
  });

  const handleSubmit = (data: NaturalFormData) => {
    mutate(
      { id: expert!.id, data: { ...data, cpf: normalizeCpf(data.cpf) } },
      {
        onSuccess() {
          setIsFormVisible(false);
          toast.success(
            formatMessage({ id: "DATA_SUCCESSFULLY_SAVED" }),
            TOAST_SUCCESS_DEFAULT_OPTIONS
          );
        },
        onError(error) {
          toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS);
        },
      }
    );
  };

  if (isFormVisible) {
    return (
      <NaturalFormPresentational
        {...methods}
        isSubmitting={isLoading}
        submitButtonText={formatMessage({ id: "SAVE" })}
        onCancel={() => setIsFormVisible(false)}
        onSubmit={handleSubmit}
      />
    );
  }

  return (
    <>
      <div>
        <LineData label={"CPF"} text={expert?.cpf && formatCpf(expert?.cpf)} />

        <LineData
          label={formatMessage({ id: "FULL_NAME" })}
          text={expert?.name}
        />

        <LineData
          label={formatMessage({ id: "NATIONALITY" })}
          text={expert?.nationality ? capitalize(expert.nationality) : ""}
        />

        <LineData
          label={formatMessage({ id: "MEETING_LANGUAGES" })}
          text={
            expert?.meetingLanguages
              ?.map(
                (language) =>
                  language.charAt(0).toUpperCase() + language.slice(1)
              )
              .join(", ") || null
          }
        />

        <LineData
          label={formatMessage({ id: "DATE_OF_BIRTH" })}
          text={expert?.naturalData?.birthdate}
        />

        <LineData label={"RG"} text={expert?.naturalData?.rg} />

        <div className="flex flex-col md:flex-row md:gap-14">
          <LineData label={"E-mail"} text={expert?.email} />
          <LineData
            label={formatMessage({ id: "TELEPHONE" })}
            text={expert?.phone}
          />
        </div>

        <LineData label={"LinkedIn"} text={expert?.linkedin} />

        <LineData label={"CEP"} text={expert?.naturalData?.cep} />

        <LineData
          label={formatMessage({ id: "STREET" })}
          text={expert?.naturalData?.street}
        />

        <div className="flex flex-col md:flex-row md:gap-14">
          <LineData
            label={formatMessage({ id: "NUMBER" })}
            text={expert?.naturalData?.addressNumber}
          />
          <LineData
            label={formatMessage({ id: "COMPLEMENT" })}
            text={expert?.naturalData?.complement}
          />
        </div>

        <div className="flex flex-col md:flex-row md:gap-14">
          <LineData
            label={formatMessage({ id: "NEIGHBORHOOD" })}
            text={expert?.naturalData?.neighborhood}
          />
          <LineData
            label={formatMessage({ id: "CITY" })}
            text={expert?.naturalData?.city}
          />
          <LineData
            label={formatMessage({ id: "STATE" })}
            text={expert?.naturalData?.state}
          />
        </div>
      </div>

      <div className="w-full md:w-fit mt-14">
        <Button fullWidth onClick={() => setIsFormVisible(true)}>
          {formatMessage({ id: "EDIT_DATA" })}
        </Button>
      </div>
    </>
  );
};
